import React from "react";
import {
    Box,
    Button,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Skeleton,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import EmptyState from "shared/ui/EmptyState";
import ResponsiveMenu from "shared/ui/ResponsiveMenu";
import { useCurrencyConversion } from "shared/contexts/CurrencyConversionContext";
import { useTranslation } from "shared/hooks/useTranslation";
import { formatSignedCurrencyAmountOrMasked, iconForPool } from "features/wallet/lib/walletDisplay";
import type { WalletAccountSection } from "features/wallet/lib/walletDisplay";
import type { LiquidityPool } from "features/wallet/api/Wallet";
import AccountRow from "features/wallet/pages/wallet-content/AccountRow";

function PanelAccountsLoadingSkeleton() {
    return (
        <Box role="status" aria-live="polite" aria-busy="true">
            {[0, 1, 2].map((row) => (
                <Box
                    key={row}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "36px minmax(0, 1fr) 36px",
                        gap: 1,
                        alignItems: "center",
                        px: 1.25,
                        py: 1,
                        borderBottom: row < 2 ? (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}` : 0,
                    }}
                >
                    <Skeleton variant="circular" width={36} height={36} />
                    <Box>
                        <Skeleton width="58%" height={18} />
                        <Skeleton width="40%" height={14} sx={{ mt: 0.35 }} />
                    </Box>
                    <Skeleton variant="circular" width={28} height={28} />
                </Box>
            ))}
        </Box>
    );
}

export interface WalletPoolsPanelListProps {
    sections: WalletAccountSection[];
    poolCount: number;
    loading: boolean;
    error: string | null;
    locale: string;
    balancesVisible: boolean;
    mutationsDisabled: boolean;
    isOnline: boolean;
    offlineTitle?: string;
    accountsPath: string;
    poolMenuAnchor: HTMLElement | null;
    subtitleForPool: (pool: LiquidityPool) => string;
    onAddAccount: () => void;
    onPoolClick: (pool: LiquidityPool) => void;
    onPoolMenuOpen: (event: React.MouseEvent<HTMLElement>, pool: LiquidityPool) => void;
    onPoolMenuClose: () => void;
    onEditClick: () => void;
    onDeleteClick: () => void;
}

export default function WalletPoolsPanelList({
    sections,
    poolCount,
    loading,
    error,
    locale,
    balancesVisible,
    mutationsDisabled,
    isOnline,
    offlineTitle,
    accountsPath,
    poolMenuAnchor,
    subtitleForPool,
    onAddAccount,
    onPoolClick,
    onPoolMenuOpen,
    onPoolMenuClose,
    onEditClick,
    onDeleteClick,
}: WalletPoolsPanelListProps) {
    const { t } = useTranslation();
    const { convertBalances } = useCurrencyConversion();

    if (loading && poolCount === 0) {
        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
                    <Skeleton width="45%" height={22} />
                    <Skeleton width="28%" height={16} sx={{ mt: 0.35 }} />
                </Box>
                <PanelAccountsLoadingSkeleton />
            </Paper>
        );
    }

    if (error) {
        return (
            <EmptyState
                icon={<AccountBalanceWalletRoundedIcon />}
                title={t("wallet.errorAccounts")}
                body={error}
            />
        );
    }

    if (poolCount === 0) {
        return (
            <EmptyState
                icon={<AccountBalanceWalletRoundedIcon />}
                title={t("wallet.emptyAccountsTitle")}
                body={t("wallet.emptyAccountsBody")}
                action={(
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onAddAccount}
                        disabled={mutationsDisabled}
                        sx={{ mt: 2, borderRadius: 999, fontWeight: 800, textTransform: "none" }}
                    >
                        {t("wallet.addPoolShortcut")}
                    </Button>
                )}
            />
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    px: 1.5,
                    py: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexShrink: 0,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                        {t("wallet.poolsTitle")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {poolCount} {poolCount === 1 ? t("wallet.account") : t("wallet.accounts")}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title={t("wallet.addPoolShortcut")}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={onAddAccount}
                                disabled={mutationsDisabled}
                                aria-label={t("wallet.addPoolShortcut")}
                                sx={{
                                    color: "primary.main",
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                    "&:hover": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16) },
                                }}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button
                        component={RouterLink}
                        to={accountsPath}
                        size="small"
                        endIcon={<ChevronRightRoundedIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            textTransform: "none",
                            fontWeight: 800,
                            fontSize: 12,
                            borderRadius: 999,
                            px: 1,
                            minWidth: 0,
                            color: "text.secondary",
                        }}
                    >
                        {t("secondaryPanel.viewAllAccounts")}
                    </Button>
                </Box>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                {sections.map((section, groupIdx) => {
                    const displayCurrencyForSection = section.key === "USD" ? "USD" : section.key;
                    const sectionTotal = convertBalances(section.balancesByCurrency, {
                        toCurrency: displayCurrencyForSection,
                    });
                    const totalColor = sectionTotal < -0.01
                        ? "error.main"
                        : sectionTotal > 0.01 ? "success.main" : "text.secondary";

                    return (
                        <Box key={section.key}>
                            {groupIdx > 0 && <Divider />}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    px: 1.5,
                                    pt: 1,
                                    pb: 0.25,
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                                    {section.key}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: totalColor,
                                        fontWeight: 800,
                                        fontVariantNumeric: "tabular-nums",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {formatSignedCurrencyAmountOrMasked(
                                        sectionTotal,
                                        displayCurrencyForSection,
                                        balancesVisible,
                                        locale,
                                    )}
                                </Typography>
                            </Box>
                            {section.pools.map((pool, index) => (
                                <AccountRow
                                    key={pool._id}
                                    pool={pool}
                                    icon={iconForPool(pool, index)}
                                    subtitle={subtitleForPool(pool)}
                                    locale={locale}
                                    balancesVisible={balancesVisible}
                                    onClick={() => onPoolClick(pool)}
                                    onMenuClick={(event) => onPoolMenuOpen(event, pool)}
                                    openLabel={t("wallet.openAccount", { name: pool.name })}
                                    menuLabel={t("common.actions")}
                                    menuDisabled={mutationsDisabled}
                                    menuTitle={!isOnline ? offlineTitle : undefined}
                                    showDivider={index < section.pools.length - 1}
                                    compact
                                />
                            ))}
                        </Box>
                    );
                })}
            </Box>

            <ResponsiveMenu
                anchorEl={poolMenuAnchor}
                open={Boolean(poolMenuAnchor)}
                onClose={onPoolMenuClose}
            >
                <MenuItem onClick={onEditClick} disabled={mutationsDisabled}>
                    {t("common.edit")}
                </MenuItem>
                <MenuItem onClick={onDeleteClick} disabled={mutationsDisabled} sx={{ color: "error.main" }}>
                    {t("common.delete")}
                </MenuItem>
            </ResponsiveMenu>
        </Paper>
    );
}
